import { Card, Stack, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    FilterList,
    FilterListItem,
    ListBase,
    ListToolbar,
    Pagination,
    SortButton,
    Title,
    TopToolbar,
    useGetIdentity,
    useListContext,
} from 'react-admin';

import {
    CreatedByUserFilter,
    EnsureDefaultCurrentUserFilter,
    isAdministrator,
} from '@/components/filters/CreatedByUserFilter';
import { ProjectListContent } from './ProjectListContent';

const ACTIVE_PROJECTS_FILTER = { phase: 'active' };

type IdentityWithAdmin = {
    id: string | number;
    administrator?: boolean;
};

export const ProjectList = () => {
    const { identity } = useGetIdentity();

    if (!identity) return null;

    const current = identity as IdentityWithAdmin;
    const isAdmin = isAdministrator(current);

    return (
        <ListBase
            resource="projects"
            storeKey="projects.active"
            // See QuoteList: avoid ra-core empty-page → page=1 while canAccess pending.
            disableAuthentication
            filter={ACTIVE_PROJECTS_FILTER}
            filterDefaultValues={
                isAdmin ? undefined : { created_by: current.id }
            }
            perPage={25}
            sort={{ field: 'project_no', order: 'DESC' }}
        >
            <ProjectListLayout />
        </ListBase>
    );
};

const ProjectListLayout = () => {
    const { data, isPending, filterValues } = useListContext();
    const { identity } = useGetIdentity();

    const isAdmin = isAdministrator(identity);
    const stickyFilterKeys = isAdmin ? ['phase'] : ['phase', 'created_by'];
    const hasFilters = Object.keys(filterValues || {}).some(
        key => !stickyFilterKeys.includes(key),
    );

    const empty = (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: '50vh', gap: 2 }}
        >
            <Typography variant="h6">No projects yet</Typography>
            <Typography variant="body2" color="text.secondary">
                Approved quotes appear here as active projects.
            </Typography>
        </Stack>
    );

    return (
        <>
            <EnsureDefaultCurrentUserFilter />
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ width: '100%' }}>
                <CreatedByUserFilter searchPlaceholder="Search projects">
                    <FilterList label="Contract" icon={<DescriptionIcon />}>
                        <FilterListItem
                            label="Pending to sign"
                            value={{ contract_pending: true }}
                        />
                    </FilterList>
                </CreatedByUserFilter>
                <Stack sx={{ width: '100%', minWidth: 0, flex: 1, p: 1 }}>
                    <Title title="Projects" />
                    <ListToolbar actions={<ProjectListActions />} />
                    {!identity || isPending ? null : !data?.length && !hasFilters ? (
                        empty
                    ) : (
                        <>
                            <Card>
                                <ProjectListContent />
                            </Card>
                            <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                        </>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

const ProjectListActions = () => (
    <TopToolbar>
        <SortButton fields={['project_no', 'name', 'id']} />
    </TopToolbar>
);
